/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-11-22 17:15:42
 * @LastEditors: Jack Deng
 * @LastEditTime: 2022-05-02 10:13:53
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorAnimation/slice/index.ts
 */
import { isCurrentPrimary } from '@/src/constrains/SalesChannelMultiLanguageSelect';
import {
  onCollapseOpen as _onCollapseOpen,
  assData,
  defaultStatus,
  generateDefaultState,
  hasError,
} from '@/src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  getSalesChannelContentAnimation,
  postSalesChannelContentAnimation,
} from '@/src/request/salesChannel/content';
import { randomNumber } from '@/src/tools/helper';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import {
  addDraggableCollapse,
  deleteDraggableCollapse,
} from 'src/constrains/DraggableModules/reduxFunctions';
import {
  EnumReducers,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getHasModified as _getHasModified,
  getLoaded as _getLoaded,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getSubmitStamp as _getSubmitStamp,
} from 'src/pages/SalesChannelContentEditor/slice/selectors';
import { msg } from 'src/tools/message';
import {
  AnimationEffection,
  AnimationType,
  EntranceOption,
  convertDataFromServerToRedux,
  fadeDirection,
  generateEntranceAnimation,
  generateHoverAnimation,
  slideDirection,
  zoomDirection,
} from '../tool';

export const fetchAnimation = createAsyncThunk(
  'fetchSalesChannelContentAnimation',
  async (data: any) => {
    const { uuid } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getSalesChannelContentAnimation(uuid);
    return {
      response,
      isPrimary,
    };
  },
);

export const submitAnimation = createAsyncThunk(
  'submitSalesChannelContentAnimation',
  async ({ body }: any) => {
    const response = await postSalesChannelContentAnimation(body);
    return {
      response,
    };
  },
);

export const getState = () => {
  return _getState(EnumReducers.animation);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.animation, id);
};

export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.animation, id);
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.animation);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.animation);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.animation);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.animation);
};

export const getLoaded = () => {
  return _getLoaded(EnumReducers.animation);
};

export const getAnimations = () => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.animation].animationList,
    (v) => {
      return v;
    },
  );
};

export const getAnimation = (id: any) => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.animation].animationList,
    (animationList) => {
      const animation = animationList.find((v) => v.uuid === id);
      return animation;
    },
  );
};

const _defaultState = generateDefaultState([]);

const defaultState = {
  ..._defaultState,
  ...defaultStatus,
  animationList: [],
};

const salesChannelContentAnimation = createSlice({
  name: 'salesChannelContentAnimation',
  initialState: defaultState,
  reducers: {
    resetData(state) {
      assData(state, defaultState);
    },
    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      _onCollapseOpen(state, idArr);
    },
    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    onDuplicate(state, action) {
      const id = randomNumber();
      const { uuid } = action.payload;
      const item = state.animationList.find((v) => v.uuid === uuid);
      if (item) {
        state.animationList.push({
          ...item,
          uuid: id,
          isNew: true,
        });
        addDraggableCollapse(state, id);
        state.hasModified = true;
      }
    },
    onDelete(state, action) {
      const { uuid } = action.payload;
      const index = state.animationList.findIndex((v) => v.uuid === uuid);
      if (index !== -1) {
        state.animationList.splice(index, 1);
        deleteDraggableCollapse(state, uuid);
        state.hasModified = true;
      }
    },
    onChangeField(state, action) {
      const { value, prop, uuid } = action.payload;
      const index = state.animationList.findIndex((v) => v.uuid === uuid);
      if (index !== -1) {
        const original = state.animationList[index];
        state.animationList[index][prop] = value;
        if (prop === 'animationType') {
          switch (value) {
            case AnimationType.EntranceEffect: {
              state.animationList[index] = {
                uuid: original.uuid,
                isNew: original.isNew,
                name: original.name,
                ...generateEntranceAnimation(),
              };
              break;
            }
            case AnimationType.HoverEffect: {
              state.animationList[index] = {
                uuid: original.uuid,
                isNew: original.isNew,
                name: original.name,
                ...generateHoverAnimation(),
              };
              break;
            }
          }
        }
        if (prop === 'option') {
          switch (value) {
            case EntranceOption.TOGETHER: {
              delete state.animationList[index]['intervalMilliseconds'];
              break;
            }
            case EntranceOption.ONE_BY_ONE: {
              state.animationList[index]['intervalMilliseconds'] = 400;
              break;
            }
          }
        }
        if (prop === 'effect') {
          switch (value) {
            case AnimationEffection.FADE: {
              state.animationList[index]['direction'] = fadeDirection.FADE_IN;
              break;
            }
            case AnimationEffection.SLIDE: {
              state.animationList[index]['direction'] =
                slideDirection.SLIDE_IN_DOWN;
              break;
            }
            case AnimationEffection.ZOOMING: {
              state.animationList[index]['direction'] = zoomDirection.ZOOM_IN;
              break;
            }
          }
        }
        state.hasModified = true;
      }
    },
    onAddAnimation(state, action) {
      const list = state.animationList;
      const id = randomNumber();
      state.animationList.push({
        isNew: true,
        uuid: id,
        name: `Transition Effect ${list.length + 1}`,
        animationType: AnimationType.EntranceEffect,
        effect: AnimationEffection.FADE,
        direction: fadeDirection.FADE_IN,
        option: EntranceOption.TOGETHER,
        durationMilliseconds: 1000,
        intervalMilliseconds: 400,
      });
      state.collapseErrorMsgDisplay[id] = { hasError: false, display: false };
      state.collapseOpen[id] = false;
      state.hasModified = true;
    },
    validation(state) {
      hasError(state, state.collapseErrorMsgDisplay || {});
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAnimation.pending, (state) => {
      state.hasModified = false;
    });
    builder.addCase(fetchAnimation.fulfilled, (state, action) => {
      const { response } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      assData(state, _data);
    });
    builder.addCase(submitAnimation.fulfilled, (state, action) => {
      // const { response } = action.payload;
      msg.success(getI18nFromGeneral('general.status.changessavesuccessfully'));
      // assData(state, { ...response });
      state.hasModified = false;
    });
  },
});

export const {
  resetData,
  onCollapseOpen,
  onErrorChange,
  validation,
  onAddAnimation,
  onChangeField,
  onDuplicate,
  onDelete,
} = salesChannelContentAnimation.actions;

export default salesChannelContentAnimation.reducer;
